import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Megaphone, Plus, Settings } from 'lucide-react'

import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/stores/auth'
import type {
  Campaign,
  CampaignListResponse,
  CampaignType,
} from '@sdr-agend/admin-schemas'
import { CAMPAIGN_TYPES } from '@sdr-agend/admin-schemas'

interface ActiveFilter {
  value: '' | 'true' | 'false'
}

const TYPE_LABELS: Record<CampaignType, string> = {
  confirmation: 'Confirmação',
  lead_new: 'Lead Novo',
  reactivation: 'Reativação',
}

/**
 * `/admin/campaigns` — list view with filters by type + active state.
 *
 * RBAC:
 *   - All authenticated users can read.
 *   - "Nova Campaign" CTA is visible for admin/manager only.
 */
export function CampaignsPage(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const canMutate = role === 'admin' || role === 'manager'

  const [typeFilter, setTypeFilter] = useState<CampaignType | ''>('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter['value']>('')

  const queryKey = useMemo(
    () => ['admin-campaigns', { typeFilter, activeFilter }],
    [typeFilter, activeFilter],
  )

  const { data, isLoading, isError } = useQuery<CampaignListResponse>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (typeFilter !== '') params.set('type', typeFilter)
      if (activeFilter !== '') params.set('active', activeFilter)
      const qs = params.toString()
      return api<CampaignListResponse>(`/admin/campaigns${qs === '' ? '' : `?${qs}`}`)
    },
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Campaigns</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie cadência, templates e prompts para os fluxos de confirmação, lead-novo e
            reativação.
          </p>
        </div>
        <div className="flex gap-2">
          {canMutate && (
            <Button asChild>
              <Link to="/admin/campaigns/new">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Nova Campaign
              </Link>
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select
          aria-label="Filtrar por tipo"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as CampaignType | '')}
          className="max-w-[14rem]"
        >
          <option value="">Todos os tipos</option>
          {CAMPAIGN_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filtrar por status ativo"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as ActiveFilter['value'])}
          className="max-w-[14rem]"
        >
          <option value="">Ativas + inativas</option>
          <option value="true">Apenas ativas</option>
          <option value="false">Apenas inativas</option>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Atualizado em</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-destructive">
                  Erro ao carregar campaigns.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && (data?.campaigns ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  Nenhuma campaign encontrada.
                </TableCell>
              </TableRow>
            )}
            {(data?.campaigns ?? []).map((c: Campaign) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-2">
                    <Megaphone className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    {c.name}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="rounded-md border px-2 py-0.5 text-xs uppercase tracking-wide">
                    {TYPE_LABELS[c.type]}
                  </span>
                </TableCell>
                <TableCell>
                  {c.active ? (
                    <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                      Ativa
                    </span>
                  ) : (
                    <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      Inativa
                    </span>
                  )}
                </TableCell>
                <TableCell>{new Date(c.updatedAt).toLocaleString('pt-BR')}</TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="ghost" size="sm">
                    <Link to={`/admin/campaigns/${c.id}`} aria-label={`Abrir ${c.name}`}>
                      <Settings className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default CampaignsPage
